import { useEffect, useRef } from 'react';
import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { makeStyles } from '@material-ui/core/styles';
import { MenuItem } from '@material-ui/core';
import { FormProvider, useForm } from 'react-hook-form';
import _ from 'lodash';
import NgpTwoButtonDialog from '../../dialog-two-button/NgpTwoButtonDialog';
import NgpForm from '../../form/NgpForm';
import FormSection from '../../form/FormSection';
import { AUTO_COMPLETE_DEFAULT, MANUAL_ERROR_TYPE, USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';
import NgpSelect from '../../form/NgpSelect';
import NgpTextField from '../../form/NgpTextField';
import {
    ACTIVE_DIRECTORY_GROUP_LABEL,
    ASSOCIATED_ROLE_FIELD,
    GROUP_NAME_FIELD,
    SAML_GROUP_LABEL,
} from './SsoAdminConstants';
import { ALL_ROLES_KEY_VALUE } from '../users/UserConstants';
import { INTERNAL_ID_FIELD } from '../../form/FormConstants';

const useStyles = makeStyles(() => ({
    roleSelect: {
        '& .MuiSelect-select': {
            minWidth: 160,
        },
    },
}));

const groupFormId = 'frmGroupId';
const uniqueErrorText = i18n._(t`Must be unique`);
const addButtonText = i18n._(t`Add`);
const updateButtonText = i18n._(t`Update`);
const defaultFormValues = {};

export default function GroupForm({
    isActiveDirectory,
    selectedList = [],
    selectedRecord = {},
    setSelectedRecord,
    open,
    setOpen,
    onConfirm,
}) {
    const classes = useStyles();
    const methods = useForm({ ...USE_FORM_DEFAULT_OPTIONS, defaultValues: defaultFormValues });
    const internalId = useRef(null);
    const buttonText = useRef(addButtonText);
    const { handleSubmit, formState, reset, setError } = methods;
    const groupFieldLabel = isActiveDirectory ? ACTIVE_DIRECTORY_GROUP_LABEL : SAML_GROUP_LABEL;

    useEffect(() => {
        if (!_.isEmpty(selectedRecord)) {
            internalId.current = selectedRecord[INTERNAL_ID_FIELD];
            buttonText.current = selectedRecord[INTERNAL_ID_FIELD] ? updateButtonText : addButtonText;
            reset(selectedRecord);
        }
    }, [selectedRecord, reset]);

    const handleCleanup = () => {
        internalId.current = null;
        buttonText.current = addButtonText;
        setSelectedRecord(undefined);
        reset(defaultFormValues);
    };

    const handleFormSubmit = (dataToSave = {}) => {
        if (internalId) {
            _.set(dataToSave, INTERNAL_ID_FIELD, internalId.current);
        }

        if (
            _.filter(selectedList, (listItem) => {
                return (
                    _.toLower(listItem[GROUP_NAME_FIELD]) === _.toLower(dataToSave[GROUP_NAME_FIELD]) &&
                    listItem[INTERNAL_ID_FIELD] !== dataToSave[INTERNAL_ID_FIELD]
                );
            }).length > 0
        ) {
            setError(GROUP_NAME_FIELD, {
                type: MANUAL_ERROR_TYPE,
                message: uniqueErrorText,
            });
            return;
        }

        return Promise.resolve()
            .then(() => onConfirm(dataToSave))
            .then(handleCleanup);
    };

    return (
        <NgpTwoButtonDialog
            title={<Trans>Group Association</Trans>}
            confirmButtonText={buttonText.current}
            cancelButtonText={<Trans>Cancel</Trans>}
            open={open}
            setOpen={setOpen}
            onCloseFunc={handleCleanup}
            confirmButtonDisabled={!formState.isValid}
            callerWillCloseOnConfirm
            onConfirm={handleSubmit(handleFormSubmit)}
            disableBackdropClick
        >
            <FormProvider {...methods}>
                <NgpForm
                    formId={groupFormId}
                    initialized
                    loading={false}
                    showSave={false}
                    formPadding={false}
                    showRequiredFieldsTip={false}
                    submitFunction={handleFormSubmit}
                >
                    <FormSection>
                        <NgpTextField
                            fieldName={GROUP_NAME_FIELD}
                            label={i18n._(groupFieldLabel)}
                            required
                            inputProps={{
                                size: 40,
                                maxLength: 255,
                                autoComplete: AUTO_COMPLETE_DEFAULT,
                            }}
                        />

                        <NgpSelect
                            fieldName={ASSOCIATED_ROLE_FIELD}
                            label={<Trans>nGeniusPULSE Role</Trans>}
                            className={classes.roleSelect}
                            required
                        >
                            {_.map(ALL_ROLES_KEY_VALUE, (value, key) => (
                                <MenuItem key={key} value={key}>
                                    {value}
                                </MenuItem>
                            ))}
                        </NgpSelect>
                    </FormSection>
                </NgpForm>
            </FormProvider>
        </NgpTwoButtonDialog>
    );
}
